/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description application utils
 */
// todo
import _ from 'lodash';
import { KeycloakTokenParsed } from 'keycloak-js';

import { WEBSITE_URL } from 'configs/url';
import {
	ROLES_BY_PRIORITY,
	ELIGIBILITY_CUSTOMER_PAGE_ROLES,
	BILLING_CUSTOMER_PAGE_ROLES,
	INTERNAL_ADMIN_ROLE,
	SUPER_ADMIN_ROLE,
	BILLING_ADMIN_ROLE,
	BILLING_USER_ROLE,
	ANALYTICS_USER_ROLE,
} from 'configs/index';
import { APP_FEATURES } from 'configs/routes';
import { WORKFLOW_STATUS } from 'app-constants/workflow';
import { CUSTOMER_TYPE } from '@veritable/common/constants';

/**
 * parse user details from token
 */
const getUserFromToken = (tokenParsed?: KeycloakTokenParsed): App.User => {
	return {
		id: tokenParsed?.sub,
		email: tokenParsed?.email,
		username: tokenParsed?.preferred_username,
		name: tokenParsed?.name,
		firstName: tokenParsed?.given_name,
		lastName: tokenParsed?.family_name,
		displayName: _.startCase(tokenParsed?.name),
		realmRoles: (tokenParsed?.resource_access?.veritable?.roles || []) as UserManagement.IRoleName[],
	};
};

/**
 * check if user is internal admin
 */
const checkInternalAdmin = (roles: UserManagement.IUserRoleResponse[]): boolean => {
	return !!_.find(roles, { assigned: true, name: INTERNAL_ADMIN_ROLE });
};

/**
 * check intersection of valid roles
 */
const allowAccess = (userRoles: string[], roles: string[]): boolean => {
	return !_.isEmpty(_.intersection(userRoles, roles));
};

/**
 * Removes element from Dom
 */
const removeElementFromDom = (id = 'splash-screen'): void => {
	const initialLoader: HTMLElement | null = document.getElementById(id);
	if (initialLoader && initialLoader.parentNode) {
		initialLoader.parentNode.removeChild(initialLoader);
	}
};

/**
 * get user role
 */
const getUserRole = (userRoles: UserManagement.IRoleName[]): UserManagement.IRoleName | undefined => {
	return _.first(_.intersection(ROLES_BY_PRIORITY, userRoles));
};

/**
 * resolve depricated user role for archetype-1 customers - auditlogs, view only purpose
 */
const resolveDepricatedRole = (userRoles: string[]): UserManagement.IRoleName | undefined => {
	const activeRole = getUserRole(userRoles as UserManagement.IRoleName[]);
	if (activeRole) {
		return activeRole;
	}
	const adminRole: UserManagement.IDepricatedRoleName = 'VT_CUSTOMER_ADMIN';
	const userRole: UserManagement.IDepricatedRoleName = 'VT_CUSTOMER_USER';
	const order = [adminRole, userRole];
	const mapping = { [adminRole]: SUPER_ADMIN_ROLE, [userRole]: BILLING_USER_ROLE };
	const oldRole = _.first(_.intersection(order, userRoles));
	return _.get(mapping, oldRole);
};

/**
 * Check if the user should onboard
 * @param setupInfo - Setup Info
 * @returns boolean
 */
const shouldOnboard = (setupInfo: App.SetupInfo): boolean => {
	return !!setupInfo?.veritableOnboarding?.process_id && setupInfo?.veritableOnboarding?.status !== WORKFLOW_STATUS.completed;
};

/**
 * Check if the tenant has code explorer
 * @param setupInfo - Setup Info
 * @returns boolean
 */
const isCodeExplorerTenant = (setupInfo: App.SetupInfo): boolean => {
	return _.includes(setupInfo.selectedApps, APP_FEATURES.codeexplorer);
};

/**
 * Check if the code explorer is standalone
 * @param setupInfo - Setup Info
 * @returns boolean
 */
const isCodeExplorerStandalone = (setupInfo: App.SetupInfo): boolean => {
	return isCodeExplorerTenant(setupInfo) && shouldOnboard(setupInfo);
};

/**
 * Check if the user can access the app
 * @param userRoles - User roles
 * @returns boolean
 */
const shouldInitializeApp = (
	userRoles: UserManagement.IRoleName[],
): { userRole: UserManagement.IRoleName | undefined; canAccessApp: boolean } => {
	const userRole = getUserRole(userRoles);
	return { userRole, canAccessApp: allowAccess(userRoles, [userRole]) };
};

/**
 * App utils validateUserAccess
 */
const validateUserAccess = (userRoles: UserManagement.IRoleName[], customerType: App.Customer): App.UserAccess => {
	const isEligibilityUser = customerType === CUSTOMER_TYPE.eligibility;
	const isARCustomer = customerType === CUSTOMER_TYPE.billing;

	const canFileClaims = isARCustomer;
	const canAddToManualWorkList = isARCustomer;
	const showPostPaymentButton = isARCustomer;
	const hasVoidClaimAccess = isARCustomer;
	const showDraftClaimActions = isARCustomer;
	const hasWorklistAccess = isARCustomer;
	const canViewSubmittedClaimTab = isARCustomer;
	const canViewEligibilityTab = isARCustomer;
	const canViewPatientDetails = isARCustomer;
	const canViewClaimWorkstateBadge = isARCustomer;
	const canEditClaimRequest = isEligibilityUser;
	const canViewClaimActions = isARCustomer;
	const canWriteOffClaim = isARCustomer;
	const canCloseClaim = isARCustomer;
	const canViewCLPDetails = isARCustomer;
	const canPrintClaimResponse = !isARCustomer;
	const canViewSubscriptionTabs = isEligibilityUser;
	const canCheckNewClaimStatus = isEligibilityUser;

	const showClaimSummaryPaymentInfo = isARCustomer;
	const showCustomAttributes = isARCustomer;

	const PAGE_ROLES = isEligibilityUser ? ELIGIBILITY_CUSTOMER_PAGE_ROLES : BILLING_CUSTOMER_PAGE_ROLES;
	const internalAdminRole = INTERNAL_ADMIN_ROLE;
	const superAdminRole = SUPER_ADMIN_ROLE;
	const billingAdminRole = BILLING_ADMIN_ROLE;
	const billingUserRole = BILLING_USER_ROLE;
	const analyticsUserRole = ANALYTICS_USER_ROLE;

	// app role
	const { userRole, canAccessApp } = shouldInitializeApp(userRoles);
	const isInternalAdmin = allowAccess(userRoles, [internalAdminRole]);
	const isSuperAdmin = !isInternalAdmin && allowAccess(userRoles, [superAdminRole]);
	const isBillingAdmin = !isInternalAdmin && !isSuperAdmin && allowAccess(userRoles, [billingAdminRole]);
	const isBillingUser = !isInternalAdmin && !isSuperAdmin && !isBillingAdmin && allowAccess(userRoles, [billingUserRole]);
	const isAnalyticsUser =
		!isInternalAdmin && !isSuperAdmin && !isBillingAdmin && !isBillingUser && allowAccess(userRoles, [analyticsUserRole]);
	const showSettings = !isAnalyticsUser;

	const canAccessSubscriptions = allowAccess(userRoles, PAGE_ROLES.settings.organization.subscriptions);

	// User management
	const canAddUsers = allowAccess(userRoles, PAGE_ROLES.settings.organization.users.add);
	const canEditUsers = allowAccess(userRoles, PAGE_ROLES.settings.organization.users.edit);
	const canUpdatePayers = allowAccess(userRoles, PAGE_ROLES.settings.dataManagement.payers.update);

	const canEditLocations = allowAccess(userRoles, PAGE_ROLES.settings.dataManagement.locations.edit);
	const canAddLocations = allowAccess(userRoles, PAGE_ROLES.settings.dataManagement.locations.add);
	const canUpdateLocationStatus = allowAccess(userRoles, PAGE_ROLES.settings.dataManagement.locations.updateStatus);

	const canEditProviders = allowAccess(userRoles, PAGE_ROLES.settings.dataManagement.providers.edit);
	const canUpdateProviderStatus = allowAccess(userRoles, PAGE_ROLES.settings.dataManagement.providers.updateStatus);

	const canEditWorklists = allowAccess(userRoles, PAGE_ROLES.settings.dataManagement.worklists.edit);
	const canEditClaims = allowAccess(userRoles, PAGE_ROLES.settings.dataManagement.claims.edit);
	const canAddClaims = allowAccess(userRoles, PAGE_ROLES.settings.dataManagement.claims.add);

	const canAddClearinghouse = allowAccess(userRoles, PAGE_ROLES.settings.integration.clearingHouse.add);
	const canEditClearinghouse = allowAccess(userRoles, PAGE_ROLES.settings.integration.clearingHouse.edit);

	const canAccessCustomAttributes = allowAccess(userRoles, PAGE_ROLES.settings.dataManagement.customAttributes.listView);

	// todo page level access for custom attributes
	const canAssignWorklists = isInternalAdmin || isSuperAdmin || isBillingAdmin;
	const canAddWorklists = isInternalAdmin || isSuperAdmin || isBillingAdmin;
	const canDeleteWorklists = isInternalAdmin || isSuperAdmin || isBillingAdmin;

	return {
		canAddToManualWorkList,
		showPostPaymentButton,
		hasVoidClaimAccess,
		showDraftClaimActions,
		showClaimSummaryPaymentInfo,
		showCustomAttributes,
		hasWorklistAccess,
		canViewSubmittedClaimTab,
		canFileClaims,
		canViewPatientDetails,
		canViewClaimWorkstateBadge,
		canEditClaimRequest,
		canViewClaimActions,
		canWriteOffClaim,
		canCloseClaim,
		canViewCLPDetails,
		canPrintClaimResponse,
		canViewEligibilityTab,
		canViewSubscriptionTabs,

		userRole,
		canAccessApp,
		isEligibilityUser,
		isARCustomer,
		isInternalAdmin,
		isSuperAdmin,
		isBillingAdmin,
		isBillingUser,
		isAnalyticsUser,
		canAccessSubscriptions,
		showSettings,

		canAddUsers,
		canEditUsers,
		canUpdatePayers,
		canUpdateLocationStatus,
		canEditProviders,
		canUpdateProviderStatus,
		canAddClearinghouse,
		canEditClearinghouse,
		canAddLocations,
		canEditLocations,
		canEditWorklists,
		canEditClaims,
		canAddClaims,
		canAccessCustomAttributes,
		canAssignWorklists,
		canAddWorklists,
		canDeleteWorklists,
		canCheckNewClaimStatus,
	};
};

/**
 * Set cookie
 */
const setCookie = (cname: string, cvalue: string, exdays: number, domain?: string): void => {
	const d = new Date();
	const baseDomain = domain ?? WEBSITE_URL;
	d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
	const expires = 'expires=' + d.toUTCString();
	document.cookie = cname + '=' + cvalue + ';' + expires + ';domain=' + baseDomain + '; path=/';
};

// function to check if data ingestion is enabled
const isDataIngestionEnabled = (dataIngestionSettings: EntitySettings.ISettingsModel): boolean => {
	return _.some(dataIngestionSettings?.data, (item) =>
		_.some(item?.children, (child) => {
			if (child?.key === 'data_ingestion.837_ingestion.enabled') {
				return child?.value?.value;
			}
			return false;
		}),
	);
};

export {
	allowAccess,
	getUserFromToken,
	shouldOnboard,
	isCodeExplorerStandalone,
	isCodeExplorerTenant,
	removeElementFromDom,
	shouldInitializeApp,
	validateUserAccess,
	setCookie,
	getUserRole,
	resolveDepricatedRole,
	checkInternalAdmin,
	isDataIngestionEnabled,
};
